import { useEffect, useState } from "react";

/*
 * Seven weekday cards at the chosen time, and one weekday's times in full.
 *
 * Replaces the month grid, which repeated every weekday four or five times and
 * was hard to read on a phone. The history is grouped by weekday anyway — every
 * past Monday is one figure — so a card per day says the same thing without the
 * repetition. Tapping a card shows the times around the chosen one, not the
 * whole day: nobody who plays in the evening cares about 10am.
 */

type Summary = {
  weekday: number;
  name: string;
  typical: Record<string, number | null>;
  chances: Record<string, number | null>;
  byKind: Record<string, Record<string, number | null>>;
  enough: Record<string, boolean>;
  observations: number;
};

type DayRow = {
  time: string;
  typical: Record<string, number | null>;
  byKind: Record<string, Record<string, number | null>>;
  enough: Record<string, boolean>;
  hadCourt: Record<string, number>;
  observations: Record<string, number>;
};

// How many minutes either side of the chosen time the detail shows — close to
// their time, since "exact, but also nearby" is what members actually want.
const WINDOW_MINUTES = 90;

export function WeekView({
  clubId, sport, startTime, duration, onClose,
}: {
  clubId: number;
  sport: string;
  startTime: string;
  duration: number;
  onClose: () => void;
}) {
  const [days, setDays] = useState<Summary[] | null>(null);
  const [available, setAvailable] = useState(true);
  const [open, setOpen] = useState<Summary | null>(null);

  useEffect(() => {
    const query = new URLSearchParams({ club_id: String(clubId), sport, start_time: startTime });
    fetch(`/forecast/week?${query}`, { headers: { Accept: "application/json" } })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        setAvailable(Boolean(data?.available));
        setDays(data?.days ?? []);
      })
      .catch(() => setDays([]));
  }, [clubId, sport, startTime]);

  useEffect(() => {
    const escape = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      if (open) setOpen(null);
      else onClose();
    };
    window.addEventListener("keydown", escape);
    return () => window.removeEventListener("keydown", escape);
  }, [open, onClose]);

  const key = String(duration);

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-black/40 p-2 sm:p-4" onClick={onClose}>
      <div
        className="mx-auto max-w-2xl rounded-xl bg-white p-4 shadow-xl sm:p-6 dark:bg-neutral-900"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-start justify-between gap-4">
          <div>
            <h2 className="text-lg font-semibold">Which day is best at {startTime}?</h2>
            <p className="mt-0.5 text-sm text-neutral-500">
              {duration} minutes · from every past week we have watched. Tap a day for the times around {startTime}.
            </p>
          </div>
          <button type="button" onClick={onClose} className="text-neutral-400 hover:text-neutral-600" aria-label="Close">✕</button>
        </div>

        {days === null && <p className="py-8 text-center text-sm text-neutral-500">Loading…</p>}

        {days !== null && !available && (
          <p className="py-8 text-center text-sm text-neutral-500">
            No history yet for {sport} at this club. It fills in as Court Pilot watches each morning.
          </p>
        )}

        {days !== null && available && (
          <div className="grid grid-cols-7 gap-1.5 sm:gap-2">
            {days.map((day) => (
              <button
                type="button"
                key={day.weekday}
                onClick={() => setOpen(day)}
                className="flex flex-col items-center gap-1 rounded-lg border border-neutral-200 py-2 transition hover:border-brand-500 dark:border-neutral-800"
              >
                <span className="text-xs font-medium text-neutral-500">{day.name.slice(0, 3)}</span>
                <Dots value={day.typical[key]} enough={day.enough[key]} />
                <Word value={day.typical[key]} enough={day.enough[key]} />
              </button>
            ))}
          </div>
        )}

        {open && (
          <DayDetail
            clubId={clubId} sport={sport} weekday={open}
            around={startTime} durationKey={key} onBack={() => setOpen(null)}
          />
        )}
      </div>
    </div>
  );
}

// Times of one weekday, narrowed to a window around the chosen time. Fetched
// from the day endpoint using any date of that weekday, since the history is
// weekday-aggregated.
function DayDetail({
  clubId, sport, weekday, around, durationKey, onBack,
}: {
  clubId: number; sport: string; weekday: Summary; around: string; durationKey: string; onBack: () => void;
}) {
  const [rows, setRows] = useState<DayRow[] | null>(null);
  const [kinds, setKinds] = useState<string[]>([]);

  useEffect(() => {
    const date = nextDateForWeekday(weekday.weekday);
    const query = new URLSearchParams({ club_id: String(clubId), sport, date });
    fetch(`/forecast/day?${query}`, { headers: { Accept: "application/json" } })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        setRows(data?.rows ?? []);
        setKinds(data?.kinds ?? []);
      })
      .catch(() => setRows([]));
  }, [clubId, sport, weekday.weekday]);

  const near = (rows ?? []).filter((row) => withinWindow(row.time, around));

  return (
    <div className="mt-4 border-t border-neutral-200 pt-4 dark:border-neutral-800">
      <button type="button" onClick={onBack} className="mb-2 text-sm text-brand-600 hover:underline">← back to the week</button>
      <h3 className="mb-2 font-medium">{weekday.name}, around {around}</h3>

      {rows === null && <p className="py-4 text-center text-sm text-neutral-500">Loading…</p>}
      {rows !== null && near.length === 0 && (
        <p className="py-4 text-sm text-neutral-500">No history near {around} yet.</p>
      )}

      <div className="space-y-1">
        {near.map((row) => (
          <div key={row.time} className="flex items-center gap-3 text-sm">
            <span className={`w-12 tabular-nums ${row.time === around ? "font-semibold" : "text-neutral-500"}`}>
              {row.time}
            </span>
            <Dots value={row.typical[durationKey]} enough={row.enough[durationKey]} />
            <span className="text-neutral-500">{kindLine(row, durationKey, kinds)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function kindLine(row: DayRow, durationKey: string, kinds: string[]) {
  if (!row.enough[durationKey]) return "too little history";
  const byKind = row.byKind[durationKey] ?? {};
  if (kinds.length <= 1) {
    const n = byKind[kinds[0]!] ?? row.typical[durationKey] ?? 0;
    return wordFor(n);
  }
  const parts = kinds.map((k) => `${byKind[k] ?? 0} ${k}`).filter((p) => !p.startsWith("0 "));
  return parts.length > 0 ? parts.join(", ") : "usually gone";
}

// A four-dot scale filled by how many courts are usually bookable, capped at
// four. Green when there is room, amber when it is tight, red when there is
// typically nothing.
function Dots({ value, enough }: { value: number | null | undefined; enough: boolean | undefined }) {
  if (!enough || value == null) {
    return <span className="text-xs text-neutral-300 dark:text-neutral-600">····</span>;
  }
  const filled = Math.min(Math.max(value, 0), 4);
  const tone = value >= 3 ? "text-emerald-500" : value >= 1 ? "text-amber-500" : "text-rose-500";
  return (
    <span className={`text-sm ${tone}`}>
      {"●".repeat(filled)}<span className="text-neutral-300 dark:text-neutral-600">{"○".repeat(4 - filled)}</span>
    </span>
  );
}

function Word({ value, enough }: { value: number | null | undefined; enough: boolean | undefined }) {
  if (!enough || value == null) return <span className="text-[11px] text-neutral-400">—</span>;
  return <span className="text-[11px] text-neutral-500">{wordFor(value)}</span>;
}

function wordFor(value: number) {
  if (value >= 4) return "easy";
  if (value >= 3) return "good";
  if (value >= 2) return "ok";
  if (value >= 1) return "tight";
  return "gone";
}

function toMinutes(time: string) {
  const [h, m] = time.split(":").map(Number);
  return h! * 60 + m!;
}

function withinWindow(time: string, around: string) {
  return Math.abs(toMinutes(time) - toMinutes(around)) <= WINDOW_MINUTES;
}

// Any date of the given weekday works — the history is aggregated by weekday —
// so the soonest future one keeps the fetch cheap and unsurprising.
function nextDateForWeekday(weekday: number) {
  const date = new Date();
  date.setHours(12, 0, 0, 0);
  while (date.getDay() !== weekday) date.setDate(date.getDate() + 1);
  return date.toISOString().slice(0, 10);
}
